import React, {useEffect, useMemo, useState} from 'react';
import {
	CommandButtonCompat as CommandButton,
	ContextualMenuCompat as ContextualMenu,
	DefaultButtonCompat as DefaultButton,
	PrimaryButtonCompat as PrimaryButton,
	SelectionMode,
	StackCompat as Stack,
} from 'components/FluentUICompat';

import {useTranslation} from 'react-i18next';
import {InfinityListColumn} from 'components/InfinityList';
import {
	EntityList,
	EntityListColumn,
	EntityListFilter,
	getSortedFilteredItems,
} from 'components';
import {
	ARRAY_TYPE,
	DATE_TYPE,
	PRIMITIVE_TYPE,
} from 'components/EntityList/EntityUtils';
import {v4 as uuidv4} from 'uuid';
import {getFilterContent} from 'components/EntityList/EntityListFilter';
import {SearchGroup} from '../SearchCards/SearchResultGroup';
import {useTopStickySectionContext} from 'components/TopStickyComponent/TopStickySectionContext';

type SearchResultAsCardsProps = {
	entityName: string;
	currentViewKey: string;
	loading: boolean;
	searchedItems: any[];
	columns: EntityListColumn[];
	renderCard: (item: any, index?: number) => JSX.Element;
	initialShownItems?: number;
	detlaShownItems?: number;
	isResultCardFormat: boolean;
};

type SearchResultProps = SearchResultAsCardsProps & {
	quickFilterColumns: string[];
	renderEntityList: (EntityList: React.ReactElement) => React.ReactElement;
};

export const SearchResult: React.FC<SearchResultProps> = props => {
	const {
		entityName,
		currentViewKey,
		isResultCardFormat,
		loading,
		searchedItems,
		columns,
		quickFilterColumns,
		renderEntityList,
	} = props;
	const resultAsCards = useMemo(() => {
		const columnsFilter = columns.filter(x =>
			quickFilterColumns.includes(x.key),
		);
		return <SearchResultAsCards {...props} columns={columnsFilter} />;
	}, [isResultCardFormat, loading, currentViewKey, searchedItems]);

	const resultAsList = useMemo(() => {
		if (
			isResultCardFormat ||
			loading ||
			currentViewKey !== entityName.toLowerCase()
		) {
			return <></>;
		}
		const listStyle = {root: {paddingLeft: 10}};
		const list = (
			<EntityList
				items={searchedItems}
				columns={columns}
				hideExcelButton={true}
				selectionMode={SelectionMode.none}
				styles={listStyle}
				sticky
			/>
		);
		return renderEntityList(list);
	}, [isResultCardFormat, loading, currentViewKey, searchedItems]);

	return (
		<>
			{resultAsCards}
			{resultAsList}
		</>
	);
};

export const SearchResultAsCards: React.FC<
	SearchResultAsCardsProps
> = props => {
	const {t} = useTranslation('features/search');

	const {
		entityName,
		currentViewKey,
		searchedItems,
		columns,
		renderCard,
		initialShownItems,
		detlaShownItems,
		isResultCardFormat,
		loading,
	} = props;

	const entityNameLower = entityName.toLowerCase();

	const [appliedFilters, setAppliedFilters] = useState<EntityListFilter[]>([]);
	const [selectedFilters, setSelectedFilters] = useState<EntityListFilter[]>(
		[],
	);
	const {setStickyChildren} = useTopStickySectionContext();

	useEffect(() => {
		setAppliedFilters([]);
		setSelectedFilters([]);
	}, [searchedItems]);

	const sortedFilteredItems = useMemo(
		() => getSortedFilteredItems(searchedItems, appliedFilters, columns),
		[searchedItems, appliedFilters],
	);

	const compQuickFilter = useMemo(() => {
		const columnsFilterItems = getFilterContent(
			columns,
			sortedFilteredItems,
			selectedFilters,
			setSelectedFilters,
			true,
			true,
		);

		return (
			<Stack
				horizontal
				style={{margin: 'auto', height: 50}}
				tokens={{childrenGap: 8}}
			>
				{columns.map((col, index) => {
					return (
						<SearchResultQuickFilter
							key={index}
							col={col}
							filterItem={columnsFilterItems.at(index)}
							appliedFilters={appliedFilters}
							selectedFilters={selectedFilters}
							setAppliedFilters={setAppliedFilters}
							setSelectedFilters={setSelectedFilters}
						/>
					);
				})}
			</Stack>
		);
	}, [columns, appliedFilters, selectedFilters, sortedFilteredItems]);

	const childrenAll = useMemo(
		() => sortedFilteredItems.map((x, index) => renderCard(x, index)),
		[sortedFilteredItems, renderCard],
	);

	useEffect(() => {
		const stickyObject = compQuickFilter;

		const stickyChild = {
			key: `quickFilter` + entityNameLower,
			sortPriority: 20,
			height: 50,
			stickyObject,
			isHidden: currentViewKey !== entityNameLower || !isResultCardFormat,
		};
		setStickyChildren(prev => [
			...prev.filter(x => x.key !== `quickFilter` + entityNameLower),
			stickyChild,
		]);
	}, [
		currentViewKey,
		entityNameLower,
		isResultCardFormat,
		compQuickFilter,
		setStickyChildren,
	]);

	return (
		<>
			{['all', entityNameLower].includes(currentViewKey) &&
				isResultCardFormat && (
					<Stack.Item style={{marginTop: '10px'}}>
						<SearchGroup
							entityName={t(entityName)}
							childrenAll={childrenAll as any[]}
							initialShownItems={initialShownItems}
							detlaShownItems={detlaShownItems}
							loading={loading}
						></SearchGroup>
					</Stack.Item>
				)}
		</>
	);
};

type SearchResultQuickFilterProps = {
	col: EntityListColumn;
	filterItem: React.ReactElement | undefined;
	appliedFilters: EntityListFilter[];
	selectedFilters: EntityListFilter[];
	setAppliedFilters: (appliedFilters: EntityListFilter[]) => void;
	setSelectedFilters: (selectedFilters: EntityListFilter[]) => void;
};

const SearchResultQuickFilter: React.FC<
	SearchResultQuickFilterProps
> = props => {
	const {
		col,
		filterItem,
		appliedFilters,
		selectedFilters,
		setAppliedFilters,
		setSelectedFilters,
	} = props;

	const {t} = useTranslation('features/search');

	const [visible, setVisible] = React.useState(false);

	const handleApplyFilters = () => {
		setAppliedFilters([...selectedFilters]);
	};

	const handleRemoveFilters = () => {
		setAppliedFilters([]);
		setSelectedFilters([]);
	};

	const buttonId = col.key + 'quickFilter';

	const propsContextualMenu = useMemo(() => {
		const onRender = () => (
			<div style={{padding: 10}}>
				{filterItem as any}
				<Stack horizontal tokens={{childrenGap: 10}}>
					<PrimaryButton text={t('ApplyFilter')} onClick={handleApplyFilters} />
					<DefaultButton
						text={t('RemoveFilter')}
						onClick={handleRemoveFilters}
						style={{marginLeft: 10}}
					/>
				</Stack>
			</div>
		);

		const itemFirst = {key: uuidv4(), onRender};

		return {
			shouldFocusOnMount: true,
			items: [itemFirst],
		};
	}, [filterItem, selectedFilters, appliedFilters]);

	const propsCommandButton = useMemo(() => {
		const selectedOptions = appliedFilters.filter(y => y.columnKey === col.key);

		const filterText =
			col.name +
			' ' +
			(selectedOptions.length > 0 ? `(${selectedOptions.length})` : '');

		return {
			text: filterText,
			onClick: () => setVisible(true),
			id: buttonId,
			iconProps: selectedOptions.length > 0 ? {iconName: 'Filter'} : undefined,
			menuProps: propsContextualMenu,
		};
	}, [visible, filterItem, selectedFilters, appliedFilters]);

	return (
		<>
			<CommandButton {...propsCommandButton} />
			{visible && (
				<ContextualMenu target={`#${buttonId}`} {...propsContextualMenu} />
			)}
		</>
	);
};

export const getEntityListColumns = (
	columnsInfinity: InfinityListColumn[],
	primitiveColumns: string[],
	arrayColumns: string[],
	dateColumns: string[],
	omitColumns: string[],
) => {
	const notSortableCol = {sortable: false};
	const filterableCol = {sortable: false, filterable: true};
	const columns: EntityListColumn[] = columnsInfinity
		.filter(x => !omitColumns.includes(x.key))
		.map(x => {
			if (primitiveColumns.includes(x.key)) {
				return {
					...x,
					...filterableCol,
					dataType: PRIMITIVE_TYPE,
				};
			}
			if (arrayColumns.includes(x.key)) {
				return {
					...x,
					...filterableCol,
					dataType: ARRAY_TYPE,
				};
			}
			if (dateColumns.includes(x.key)) {
				return {
					...x,
					...filterableCol,
					dataType: DATE_TYPE,
				};
			}

			return {...x, ...notSortableCol};
		});

	return columns;
};
